import { useState } from "react";
import {
  Waves, Dumbbell, Trees, Music, Gamepad2, BookOpen,
  UtensilsCrossed, Users, Baby, Dog, Bike, Heart,
  Sparkles, Mountain, Tent, Trophy
} from "lucide-react";

const categories = [
  {
    id: "clubhouse",
    label: "Clubhouse",
    icon: Sparkles,
    items: [
      "Temperature Controlled Swimming Pool with Jacuzzi",
      "Shallow Pool Lounges",
      "Aqua Gym",
      "Kids Pool",
      "Multipurpose Hall",
      "Dining Hall",
      "Indoor Sports",
      "Business Lounge",
      "Terrace Party Area",
      "Snack Bar",
      "Library",
      "Aerobics Studio",
      "Yoga Studio",
      "Indoor Badminton Courts",
      "Squash Courts",
      "Preview Theatre",
      "Provision for Spa",
      "Gymnasium",
      "Executive Guest Room",
      "Suite Rooms",
      "Party Lawn",
    ],
  },
  {
    id: "sports",
    label: "Sports Park",
    icon: Trophy,
    items: [
      "Futsal Court",
      "Pickle Ball Courts",
      "Basketball Court",
      "Tennis Court",
      "Box Cricket",
      "Open Playground",
      "Volleyball Court",
      "Skating Rink",
      "Tree House",
      "Cricket Nets",
      "Elderly Fitness Court",
      "Pet Park",
      "Flea Market Space",
    ],
  },
  {
    id: "outdoors",
    label: "Outdoors & Wellness",
    icon: Trees,
    items: [
      "The Arc (Elevated Walkway)",
      "Miyawaki Forest Walk",
      "Synthetic Track",
      "Central Park",
      "Reflexology",
      "Yoga Lawn",
      "Meditation Deck",
      "Sacred Lawn",
      "Prayer Avenue",
      "Mandapa",
    ],
  },
  {
    id: "social",
    label: "Social & Events",
    icon: Users,
    items: [
      "Celebration Lawn",
      "Festival Lawn",
      "Event Lawn",
      "Interactive Lawn",
      "Amphitheatre",
      "Social Pit",
      "Social Pods",
      "Elderly Plaza",
      "Water Island with Sculpture",
    ],
  },
  {
    id: "kids",
    label: "Kids & Family",
    icon: Baby,
    items: [
      "Kids Play Area",
      "Toddlers Play Area",
      "Sandpit",
      "Chess Court",
      "Senior Citizen Court",
      "School Bus Bay",
    ],
  },
];

const iconMap = {
  clubhouse: Waves,
  sports: Dumbbell,
  outdoors: Mountain,
  social: Music,
  kids: Heart,
};

export default function Amenities() {
  const [activeCategory, setActiveCategory] = useState("clubhouse");
  const active = categories.find((c) => c.id === activeCategory);

  return (
    <section
      id="amenities"
      data-testid="amenities-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="overline mb-4">World-Class Living</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
            Amenities designed to
            <br />
            <span className="italic text-[#C5A059]">delight all ages</span>.
          </h2>
          <div className="gold-line mx-auto mt-6" />
        </div>

        {/* Bento Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left - Image + count */}
          <div className="lg:col-span-5 relative img-zoom">
            <img
              src="../images/build4.png"
              alt="Swimming Pool"
              className="w-full h-[400px] lg:h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-8">
              <div className="stat-number text-5xl font-bold text-white">70+</div>
              <div className="text-white/80 text-sm uppercase tracking-[0.2em] mt-1">
                Premium Amenities
              </div>
            </div>
          </div>

          {/* Right - Tabs + List */}
          <div className="lg:col-span-7">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    data-testid={`amenity-tab-${cat.id}`}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wider font-medium transition-all duration-300 ${
                      activeCategory === cat.id
                        ? "bg-[#C5A059] text-white"
                        : "bg-[#FAF9F6] text-[#5C5C59] hover:bg-[#C5A059]/10 hover:text-[#C5A059]"
                    }`}
                  >
                    <Icon size={16} />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Amenity Items */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {active?.items.map((item, i) => (
                <div
                  key={item}
                  data-testid={`amenity-item-${i}`}
                  className="flex items-center gap-3 py-2.5 border-b border-[#E8E4D9]/60 group"
                >
                  <div className="w-1.5 h-1.5 bg-[#C5A059] flex-shrink-0 group-hover:scale-150 transition-transform" />
                  <span className="text-[#1A1A1A] text-sm md:text-base">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom image strip */}
        {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          <div className="img-zoom h-48">
            <img
              src="https://images.unsplash.com/photo-1761971976592-58882ab1cacc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbHViaG91c2UlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzUwNTgwNDV8MA&ixlib=rb-4.1.0&q=85"
              alt="Clubhouse Interior"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="img-zoom h-48">
            <img
              src="https://images.pexels.com/photos/7174113/pexels-photo-7174113.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Sports Facility"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="img-zoom h-48">
            <img
              src="https://images.pexels.com/photos/35215414/pexels-photo-35215414.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Garden Area"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="img-zoom h-48">
            <img
              src="https://images.unsplash.com/photo-1732364756082-90a8466c50dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzUwNTgwNDN8MA&ixlib=rb-4.1.0&q=85"
              alt="Luxury Interior"
              className="w-full h-full object-cover"
            />
          </div>
        </div> */}
      </div>
    </section>
  );
}
